import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const AvatarCropModal = ({ src, onClose, onSave }) => {
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false); // Novo estado para controlar o carregamento da imagem

  useEffect(() => {
    if (src) {
      setImageLoaded(false); // Resetar estado de carregamento
      const img = new Image();
      img.onload = () => {
        imgRef.current = img;
        setImageLoaded(true);
        const newCrop = makeAspectCrop(
          {
            unit: "%",
            width: 90,
          },
          img.naturalWidth,
          img.naturalHeight
        );
        setCrop(centerCrop(newCrop, img.naturalWidth, img.naturalHeight));
      };
      img.src = src;
    }
  }, [src]);

  const getCroppedImg = useCallback(async () => {
    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const currentCrop = completedCrop;

    // Adicionar verificação de tipo para image
    if (
      !currentCrop ||
      !imageLoaded ||
      !(image instanceof HTMLImageElement) ||
      !canvas
    ) {
      // Adicionar imageLoaded
      console.error(
        "Erro: Imagem, recorte ou tela não estão prontos para recorte.",
        { currentCrop, image, canvas, imageLoaded }
      );
      return null;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");

    canvas.width = currentCrop.width;
    canvas.height = currentCrop.height;

    ctx.drawImage(
      image,
      currentCrop.x * scaleX,
      currentCrop.y * scaleY,
      currentCrop.width * scaleX,
      currentCrop.height * scaleY,
      0,
      0,
      currentCrop.width,
      currentCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.95
      ); // Qualidade 0.95 para JPEG
    });
  }, [completedCrop]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        onSave(croppedBlob);
        onClose(); // Fecha o modal após guardar
      }
    } catch (error) {
      console.error("Erro ao guardar imagem recortada:", error);
      // toast.error("Erro ao guardar imagem recortada."); // Adicionar toast se necessário
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="relative p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <h3 className="text-2xl font-bold text-center mb-4">
          Recortar Imagem de Perfil
        </h3>
        <div className="mt-3 text-center">
          {src && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1} // Aspect ratio 1:1 para avatares
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={src}
                // Remover onLoad={onImageLoaded}
              />
            </ReactCrop>
          )}
          <canvas
            ref={previewCanvasRef}
            style={{ display: "none" }} // Esconde o canvas de preview
          />
          <div className="flex justify-end mt-4 space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={loading || !completedCrop || !imageLoaded} // Desabilitar se a imagem não estiver carregada
            >
              {loading ? "A guardar..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarCropModal;
